import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Reconciles the three panel styles that grew up separately: the legacy
 * `border-neutral-200 bg-white p-6 dark:…`, the brand panel, and the catalog card
 * — which is the best-looking surface in the app and becomes `interactive` here.
 *
 * Hairline borders carry the separation and shadow is reserved for genuine
 * elevation, which is what makes the result read sharp rather than soft.
 */
const card = cva("rounded-xl border", {
  variants: {
    tone: {
      base: "border-[var(--border)] bg-[var(--surface-1)]",
      brand: "border-[var(--accent-border)] bg-[var(--surface-1)]",
      sunken: "border-transparent bg-[var(--surface-2)]",
      /** Empty and error states — dashed, no fill. */
      dashed: "border-dashed border-[var(--border-strong)] bg-transparent",
    },
    pad: { none: "", sm: "p-4", base: "p-5", lg: "p-6" },
    interactive: {
      true: "shadow-sm transition hover:border-[var(--accent)] hover:shadow-md",
      false: "",
    },
  },
  defaultVariants: { tone: "base", pad: "base", interactive: false },
});

type CardProps = React.ComponentPropsWithoutRef<"div"> & VariantProps<typeof card>;

export function Card({ tone, pad, interactive, className, ...props }: CardProps) {
  return <div className={cn(card({ tone, pad, interactive }), className)} {...props} />;
}

/**
 * Class string for elements that cannot be a <div> — a card-shaped <a>, for
 * instance. Merge extra classes with cn() rather than passing them here.
 */
export function cardClasses(variants: VariantProps<typeof card> = {}) {
  return card(variants);
}

/**
 * The lesson player stacks nine sibling panels with no shared wrapper, each
 * inventing its own border and padding. This gives them one, with the heading
 * bound to the section so a screen reader can navigate by landmark.
 */
type PanelSectionProps = React.ComponentPropsWithoutRef<"section"> & {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  tone?: VariantProps<typeof card>["tone"];
  headingId?: string;
};

export function PanelSection({
  title,
  description,
  actions,
  tone = "base",
  headingId,
  className,
  children,
  ...props
}: PanelSectionProps) {
  const id = headingId ?? `panel-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <section aria-labelledby={id} className={cn(card({ tone, pad: "base" }), className)} {...props}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={id} className="text-lg font-semibold text-[var(--text-heading)]">
            {title}
          </h2>
          {description ? (
            <p className="mt-1 text-sm text-[var(--text-muted)]">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {children ? <div className="mt-4">{children}</div> : null}
    </section>
  );
}
